/**
 * 고객사 — 사용량과 청구
 * ---------------------------------------------------------------------------
 * 고객이 알고 싶은 것은 셋이다.
 *   1. 지금 얼마나 쓰고 있나 (한도에 얼마나 가까운가)
 *   2. 이번 달에 얼마가 나올 것 같나
 *   3. 지난 청구서와 납부 상태
 *
 * 한도 초과는 월말에 알면 늦다. 사용량 막대를 가장 위에 둔다.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Empty,
  Modal,
  Progress,
  Row,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import { BankOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText, qk } from '../../lib/queries';
import { downloadCsv, csvDate } from '../../lib/csv';
import { PaymentMethodPanel } from './PaymentMethodPanel';
import { CommitmentPanel } from './CommitmentPanel';
import { usePaymentSchedule } from './paymentSchedule';

const { Text, Paragraph } = Typography;

interface QuotaRow {
  metric_key: string;
  metric_name: string;
  /** 제품별로 따로 세는 지표면 그 제품 이름. 기업 전체 지표는 null. */
  product_name: string | null;
  unit: string;
  current_value: number;
  limit_value: number;
  usage_ratio: number;
  is_exceeded: boolean;
  /**
   * 포함량 없이 쓴 만큼 내는 지표인가 (AI 보고서 등).
   *
   * 이런 지표를 한도 막대로 그리면 한 건만 써도 「0 중 1」이 되어 빨간
   * 딱지가 붙는다. 실제로는 정상이고, 안 쓰면 0원이다.
   */
  is_pay_per_use: boolean;
  overage_allowed: boolean | null;
  /** 묶음(overage_step) 하나당 금액(원) */
  overage_price: number | null;
  /** 단가를 매기는 묶음 크기. 1GB 당이면 1073741824 */
  overage_step: number | null;
}

/** 제품 하나의 구독. 기업은 제품마다 별도 구독을 갖는다. */
interface Subscription {
  subscription_id: string;
  product_code: string;
  product_name: string;
  plan_name: string;
  plan_code: string;
  status: string;
  units: number;
  billing_unit: 'FLAT' | 'PER_USER' | 'PER_SITE' | 'PER_UNIT';
  unit_label: string | null;
  included_units: number;
  base_price: number;
  unit_price: number;
  billing_cycle: string;
  current_period_start: string;
  current_period_end: string;
  trial_ends_at: string | null;
  overrides: Array<{
    override_type: string;
    numeric_value: number;
    reason: string;
    valid_until: string | null;
  }>;
}

interface InvoiceRow {
  invoice_id: string;
  invoice_no: string;
  status: string;
  period_start: string;
  period_end: string;
  due_at: string | null;
  total_amount: number;
  paid_amount: number;
  /** 국세청 승인번호. 세금계산서가 발급되면 채워진다 */
  tax_invoice_no: string | null;
  tax_invoice_at: string | null;
  /** 실제로 들어온 돈의 수단. 여러 번 나눠 냈으면 둘 이상일 수 있다 */
  pay_methods: string[] | null;
}

/** 결제 수단을 사람이 읽는 말로 */
const PAY_METHOD: Record<string, string> = {
  TRANSFER: '계좌이체',
  CARD: '카드',
  MANUAL: '수기 기록',
};

interface EstimateLine {
  itemType: string;
  description: string;
  amount: number;
  /** 몇 개인가. 사용자 수, 초과 묶음 수. */
  quantity?: number;
  /** 하나당 얼마인가. */
  unitPrice?: number;
}

interface Estimate {
  lines: EstimateLine[];
  subtotalAmount: number;
  discountAmount: number;
  /** 초과 사용료 합계. */
  overageAmount?: number;
  vatAmount: number;
  totalAmount: number;
  periodStart?: string;
  periodEnd?: string;
  /**
   * 지금 실제로 청구되는 금액인가.
   *
   * 체험 기간에는 `false` 가 온다. 그때 아래 금액은 **체험이 끝나면 매달
   * 얼마가 나가는지** 를 미리 보여 주는 안내용 견적이다. 이 구분을 화면에
   * 적어 주지 않으면 체험 중인 고객이 "왜 벌써 돈이 나오지" 로 읽는다.
   */
  billable?: boolean;
  /** 청구 대상이 아닐 때의 사유 (예: 체험 기간) */
  notice?: string;
}

const won = (v: number | null | undefined) => `${Number(v ?? 0).toLocaleString('ko-KR')}원`;

/**
 * 구독 상태.
 *
 * 영문 코드를 그대로 띄우면 「TRIALING」이 뜬다 — 고객이 읽을 말이 아니다.
 */
const SUBSCRIPTION_STATUS: Record<string, { text: string; color: string }> = {
  TRIALING: { text: '무료 체험', color: 'blue' },
  ACTIVE: { text: '이용 중', color: 'green' },
  PAST_DUE: { text: '납부 지연', color: 'orange' },
  GRACE: { text: '유예 기간', color: 'orange' },
  SUSPENDED: { text: '정지', color: 'red' },
  CANCELED: { text: '해지', color: 'default' },
  EXPIRED: { text: '종료', color: 'default' },
};

/**
 * 청구 내역 한 줄의 구분.
 *
 * 금액이 어디서 왔는지 한눈에 갈라 보이게 한다 — 고정으로 나가는 것(기본료),
 * 사람 수에 따른 것(사용자), 쓴 만큼 붙은 것(초과), 깎인 것(할인).
 */
const LINE_TYPE: Record<string, { text: string; color: string }> = {
  SUBSCRIPTION: { text: '기본료', color: 'blue' },
  SEAT: { text: '사용자', color: 'geekblue' },
  OVERAGE: { text: '초과', color: 'orange' },
  DISCOUNT: { text: '할인', color: 'green' },
};

const INVOICE_STATUS: Record<string, { text: string; color: string }> = {
  ISSUED: { text: '발행', color: 'blue' },
  PAID: { text: '납부완료', color: 'green' },
  OVERDUE: { text: '연체', color: 'red' },
  VOID: { text: '무효', color: 'default' },
  REFUNDED: { text: '환불', color: 'orange' },
};

export default function TenantBillingPage() {
  const [detailId, setDetailId] = useState<string | null>(null);

  /*
   * 어느 달의 사용량을 보는가. 기본은 이번 달이다.
   *
   * 사용량은 **달마다 0 에서 다시 센다**(트래픽·AI 보고서처럼 쌓이는 값).
   * 저장 용량처럼 「지금 얼마인가」인 값은 이월된다. 지난 달을 고를 수 있게
   * 둔 이유는, 달이 바뀌면 지난 달 숫자를 볼 길이 사라지기 때문이다.
   */
  const [usageMonth, setUsageMonth] = useState<Dayjs>(dayjs().startOf('month'));
  const isThisMonth = usageMonth.isSame(dayjs(), 'month');

  /*
   * 결제 수단 탭에 「등록 필요 / 등록됨」을 달기 위한 값.
   * 아직 못 받았으면 undefined 로 두고 **아무 딱지도 달지 않는다** —
   * 받아 보지도 않고 「등록 필요」를 띄우면 멀쩡히 등록한 고객을 놀래킨다.
   */
  const schedule = usePaymentSchedule();
  const payMethodReady = schedule.data?.payMethodRegistered;

  const quota = useQuery<QuotaRow[], NormalizedError>({
    queryKey: ['usage', 'quota', usageMonth.format('YYYY-MM')],
    queryFn: async () =>
      (
        await apiClient.get<QuotaRow[]>('/usage/quota', {
          params: { period: usageMonth.format('YYYY-MM') },
        })
      ).data,
  });

  const subscriptions = useQuery<Subscription[], NormalizedError>({
    queryKey: [...qk.tenant, 'subscription'],
    queryFn: async () => (await apiClient.get<Subscription[]>('/billing/subscription')).data,
  });

  const estimate = useQuery<Estimate, NormalizedError>({
    queryKey: ['billing', 'estimate'],
    queryFn: async () => (await apiClient.get<Estimate>('/billing/estimate')).data,
    // 구독이 없으면 404 가 나므로 재시도하지 않는다.
    retry: false,
  });

  const invoices = useQuery<InvoiceRow[], NormalizedError>({
    queryKey: ['billing', 'invoices'],
    queryFn: async () => (await apiClient.get<InvoiceRow[]>('/billing/invoices')).data,
  });

  const detail = useQuery<Record<string, unknown>, NormalizedError>({
    queryKey: ['billing', 'invoice', detailId],
    queryFn: async () => (await apiClient.get(`/billing/invoices/${detailId}`)).data,
    enabled: Boolean(detailId),
  });

  // 한도를 넘은 것과, 넘어서 **막힌** 것은 다르다. 초과를 허용하는 요금제는
  // 돈을 더 낼 뿐 기능이 멈추지 않는다. 둘을 섞어 적으면 멀쩡한 기업이
  // "차단되었다" 는 빨간 글을 본다.
  const exceeded = (quota.data ?? []).filter((q) => q.is_exceeded);
  const blocked = exceeded.filter((q) => !q.overage_allowed);

  const invoiceColumns: ColumnsType<InvoiceRow> = [
    { title: '청구서 번호', dataIndex: 'invoice_no', key: 'no', width: 170 },
    {
      title: '기간',
      key: 'period',
      width: 200,
      render: (_, row) => `${row.period_start?.slice(0, 10)} ~ ${row.period_end?.slice(0, 10)}`,
    },
    {
      title: '금액',
      dataIndex: 'total_amount',
      key: 'amount',
      width: 140,
      align: 'right',
      render: (v: number) => <strong>{won(v)}</strong>,
    },
    {
      title: '납기',
      dataIndex: 'due_at',
      key: 'due',
      width: 120,
      render: (v: string | null) => (v ? new Date(v).toLocaleDateString('ko-KR') : '-'),
    },
    {
      /*
        어떻게 냈는지. 「이미 냈는데 또 왔다」 는 문의가 가장 많은데,
        목록에 수단이 없으면 담당자가 통장을 뒤져야 했다.
        실패한 결제 시도는 세지 않는다 — 받은 적이 없는 수단이다.
      */
      title: '결제 수단',
      key: 'method',
      width: 110,
      render: (_, row) => {
        const ms = row.pay_methods ?? [];
        if (ms.length === 0) {
          return <Text type="secondary">—</Text>;
        }
        return (
          <Space size={4} wrap>
            {ms.map((m) => (
              <Tag key={m} color={m === 'CARD' ? 'blue' : 'default'}>
                {PAY_METHOD[m] ?? m}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      /*
        세금계산서 승인번호. 「계산서 언제 나오나요」 가 두 번째로 많은
        문의다. 발급되면 번호와 발급일이 여기 뜬다.
      */
      title: '세금계산서',
      key: 'tax',
      width: 220,
      render: (_, row) =>
        row.tax_invoice_no ? (
          <Space direction="vertical" size={0}>
            <Text style={{ fontSize: 12 }} copyable={{ text: row.tax_invoice_no }}>
              {row.tax_invoice_no}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.tax_invoice_at ? `${row.tax_invoice_at.slice(0, 10)} 발급` : ''}
            </Text>
          </Space>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>발급 예정</Text>
        ),
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (v: string) => {
        const m = INVOICE_STATUS[v] ?? { text: v, color: 'default' };
        return <Tag color={m.color}>{m.text}</Tag>;
      },
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, row) => (
        <a onClick={() => setDetailId(row.invoice_id)}>상세</a>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {exceeded.length > 0 && (
        <Alert
          type={blocked.length > 0 ? 'error' : 'warning'}
          showIcon
          message={`${exceeded.map(quotaLabel).join(', ')} 한도를 넘었습니다`}
          description={
            blocked.length > 0
              ? `${blocked.map(quotaLabel).join(', ')} 은(는) 초과 사용이 허용되지 않아 해당 기능이 차단됩니다. 요금제를 상향하거나 사용량을 줄여 주세요.`
              : '초과분은 다음 청구서에 추가 요금으로 청구됩니다. 기능이 막히지는 않습니다.'
          }
        />
      )}

      {/*
        어느 달을 보는가.

        달이 바뀌면 쌓이는 값(트래픽·AI 보고서)은 0 에서 다시 센다. 그래서
        1일이 지나면 지난 달 숫자를 볼 길이 없어진다 — 여기서 고를 수 있게 한다.
      */}
      <Space wrap align="center">
        <Text strong>사용량</Text>
        <DatePicker
          picker="month"
          allowClear={false}
          value={usageMonth}
          onChange={(v) => v && setUsageMonth(v.startOf('month'))}
          disabledDate={(d) => d.isAfter(dayjs(), 'month')}
          style={{ width: 130 }}
        />
        {!isThisMonth && (
          <>
            <Button size="small" onClick={() => setUsageMonth(dayjs().startOf('month'))}>
              이번 달로
            </Button>
            {/*
              지난 달을 볼 때는 한도를 그때 값으로 되살릴 수 없다. 계약 수량이
              바뀐 이력을 남기지 않아서다. 모르고 보면 "그때 한도가 이랬구나"
              로 잘못 읽는다.
            */}
            <Text type="secondary" style={{ fontSize: 12 }}>
              한도는 <b>지금 계약</b> 기준으로 그린 것입니다. 그달에 확정된 금액은 「청구서」
              탭에서 보세요.
            </Text>
          </>
        )}
      </Space>

      <Row gutter={16}>
        {(quota.data ?? []).map((q) => (
          <Col span={8} key={q.metric_key} style={{ marginBottom: 16 }}>
            <Card size="small">
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                  <Text strong>
                    {q.metric_name}
                    {q.product_name && (
                      <Text type="secondary" style={{ fontWeight: 400 }}>
                        {' '}
                        · {q.product_name}
                      </Text>
                    )}
                  </Text>
                  {q.is_pay_per_use ? (
                    <Tag color="blue">사용한 만큼</Tag>
                  ) : (
                    q.is_exceeded && <Tag color="red">초과</Tag>
                  )}
                </Space>

                {/*
                  종량제 지표는 한도 막대를 그리지 않는다. 포함량이 0이라
                  분모가 0이고, 막대를 그리면 한 건만 써도 100% 빨강이 된다.
                */}
                {q.is_pay_per_use ? (
                  <Text style={{ fontSize: 20, fontWeight: 600 }}>
                    {formatValue(q.unit, q.current_value)}
                    <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
                      {' '}
                      {q.unit === 'BYTES' ? '' : '건'} 사용
                    </Text>
                  </Text>
                ) : (
                  <Progress
                    percent={Math.min(100, Math.round((q.usage_ratio ?? 0) * 100))}
                    status={q.is_exceeded ? 'exception' : undefined}
                    strokeColor={q.usage_ratio > 0.8 ? '#faad14' : undefined}
                  />
                )}

                <Text type="secondary" style={{ fontSize: 12 }}>
                  {q.is_pay_per_use
                    ? '요금제에 기본 포함 · 쓰지 않으면 0원'
                    : `${formatValue(q.unit, q.current_value)} / ${formatValue(q.unit, q.limit_value)}`}
                  {/*
                    단가는 묶음(overage_step) 하나당 금액이다.
                    예전에는 바이트당 소수 단가를 그대로 찍어 「0원/B」로 보였다.
                  */}
                  {q.overage_allowed && q.overage_price
                    ? ` · ${q.is_pay_per_use ? '' : '초과 시 '}${q.overage_price.toLocaleString('ko-KR')}원/${stepLabel(q.overage_step, q.unit)}`
                    : ''}
                </Text>
              </Space>
            </Card>
          </Col>
        ))}
        {(quota.data ?? []).length === 0 && !quota.isLoading && (
          <Col span={24}>
            <Card size="small">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  isThisMonth
                    ? '구독 중인 제품이 없어 표시할 사용량이 없습니다.'
                    : `${usageMonth.format('YYYY년 M월')}에는 집계된 사용량이 없습니다.`
                }
              />
            </Card>
          </Col>
        )}
      </Row>

      {/*
        탭을 카드 모양으로 바꾼다.
        기본(line) 탭은 글자 밑에 가는 줄 하나라, 화면 위쪽이 카드로 가득한
        이 페이지에서는 **어디가 탭인지 보이지 않았다.** 카드 모양이면 지금
        고른 탭이 종이처럼 앞으로 나와 한눈에 구분된다.
      */}
      <Tabs
        type="card"
        size="large"
        items={[
          {
            key: 'subscription',
            label: '구독',
            // 제품마다 구독이 하나씩이다. 제품별로 카드를 나눠 보여준다.
            children: subscriptions.isLoading ? (
              <Card loading />
            ) : (subscriptions.data ?? []).length === 0 ? (
              <Card>
                <Empty description="구독 중인 제품이 없습니다." />
              </Card>
            ) : (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {(subscriptions.data ?? []).map((s) => (
                  <Card
                    key={s.subscription_id}
                    size="small"
                    title={
                      <Space>
                        <strong>{s.product_name}</strong>
                        <Tag color={SUBSCRIPTION_STATUS[s.status]?.color ?? 'default'}>
                          {SUBSCRIPTION_STATUS[s.status]?.text ?? s.status}
                        </Tag>
                      </Space>
                    }
                  >
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <Descriptions bordered size="small" column={2}>
                        <Descriptions.Item label="요금제">{s.plan_name}</Descriptions.Item>
                        <Descriptions.Item label="기본료">
                          {won(s.base_price)} / 월
                        </Descriptions.Item>
                        <Descriptions.Item label="과금 방식">
                          {describeBillingUnit(s)}
                        </Descriptions.Item>
                        <Descriptions.Item label="현재 주기">
                          {s.current_period_start?.slice(0, 10)} ~{' '}
                          {s.current_period_end?.slice(0, 10)}
                        </Descriptions.Item>
                        {/*
                          체험 종료일은 언제부터 돈이 나가는지를 말해 준다.
                          상태 딱지만으로는 "언제까지 공짜인가" 를 알 수 없다.
                        */}
                        {s.trial_ends_at && (
                          <Descriptions.Item label="무료 체험" span={2}>
                            {new Date(s.trial_ends_at).toLocaleDateString('ko-KR')} 까지 · 그 다음
                            주기부터 청구됩니다
                          </Descriptions.Item>
                        )}
                      </Descriptions>

                      {(s.overrides ?? []).filter((o) => o.override_type.startsWith('DISCOUNT'))
                        .length > 0 && (
                        <Alert
                          type="success"
                          showIcon
                          message="적용 중인 할인"
                          description={
                            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                              {s.overrides
                                .filter((o) => o.override_type.startsWith('DISCOUNT'))
                                .map((o, i) => (
                                  <li key={i}>
                                    {o.override_type === 'DISCOUNT_RATE'
                                      ? `${(o.numeric_value * 100).toFixed(1)}% 할인`
                                      : `${won(o.numeric_value)} 할인`}{' '}
                                    — {o.reason}
                                    {o.valid_until
                                      ? ` (${new Date(o.valid_until).toLocaleDateString('ko-KR')}까지)`
                                      : ''}
                                  </li>
                                ))}
                            </ul>
                          }
                        />
                      )}
                    </Space>
                  </Card>
                ))}
              </Space>
            ),
          },
          {
            key: 'commitment',
            label: '약정 할인',
            // 구독 바로 옆에 둔다. 요금을 보고 "더 싸게 쓸 방법은 없나" 를
            // 떠올리는 자리가 거기다.
            children: <CommitmentPanel />,
          },
          {
            key: 'payment-methods',
            /*
              다른 탭과 달리 **아이콘과 상태를 함께** 단다.
              무료 기간이 끝나면 등록·수정이 막히고, 그때 고객이 가장 먼저
              와야 하는 곳이 여기다. 「구독·약정 할인·결제 수단·이번 달
              예상·청구서」가 전부 같은 크기 글자로 나란히 있으면 그중 무엇이
              **지금 해야 할 일**인지 보이지 않는다.
            */
            label: (
              <Space size={6}>
                <BankOutlined />
                <span>결제 수단</span>
                {payMethodReady === false && <Tag color="red">등록 필요</Tag>}
                {payMethodReady === true && <Tag color="green">등록됨</Tag>}
              </Space>
            ),
            children: <PaymentMethodPanel />,
          },
          {
            key: 'estimate',
            label: '이번 달 예상',
            children: (
              <Card loading={estimate.isLoading}>
                {estimate.isError ? (
                  <Alert type="info" showIcon message={errorText(estimate.error)} />
                ) : (
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {/*
                      체험 기간에는 **지금 낼 돈이 아니라는 것**을 먼저 말한다.
                      아래 금액은 체험이 끝난 뒤의 월 예상액이라, 그 구분이
                      없으면 "왜 벌써 돈이 나오지" 로 읽힌다.

                      예전에는 서버가 이 경우 404 를 내서 금액 자체가 안 보였다.
                      "지금은 안 내도 된다" 는 알 수 있었지만 "끝나면 얼마" 는
                      알 수 없었다 — 결제수단을 등록할지 판단할 근거가 없었다.
                    */}
                    {estimate.data?.billable === false ? (
                      <Alert
                        type="success"
                        showIcon
                        message="지금 청구되는 금액은 없습니다"
                        description={
                          <>
                            {estimate.data?.notice}
                            <br />
                            아래는 <strong>체험이 끝난 뒤 매달 청구될 예상 금액</strong>입니다.
                          </>
                        }
                      />
                    ) : (
                      <Alert
                        type="info"
                        showIcon
                        message="확정 금액이 아닙니다"
                        description="이번 달이 끝나야 사용량이 확정됩니다. 지금까지의 사용량으로 계산한 예상치입니다."
                      />
                    )}
                    {/*
                      무엇 때문에 이 금액인지 한 줄씩 보여 준다.

                      예전에는 항목 이름과 금액 두 칸뿐이라 "왜 이 금액인가"
                      를 되물어야 했다. 구분·수량·단가를 나눠 적으면 고객이
                      직접 검산할 수 있다 — 특히 **초과 사용료**는 얼마나
                      넘겨 썼고 묶음당 얼마인지가 보여야 납득이 된다.
                    */}
                    <Table<EstimateLine>
                      rowKey={(r) => `${r.itemType}:${r.description}`}
                      size="small"
                      pagination={false}
                      dataSource={estimate.data?.lines ?? []}
                      columns={[
                        {
                          title: '구분',
                          dataIndex: 'itemType',
                          key: 't',
                          width: 90,
                          render: (v: string) => {
                            const m = LINE_TYPE[v] ?? { text: v, color: 'default' };
                            return <Tag color={m.color}>{m.text}</Tag>;
                          },
                        },
                        { title: '내역', dataIndex: 'description', key: 'd' },
                        {
                          title: '수량',
                          dataIndex: 'quantity',
                          key: 'q',
                          width: 80,
                          align: 'right',
                          render: (v: number | undefined) =>
                            v === undefined || v === null ? (
                              <Text type="secondary">—</Text>
                            ) : (
                              v.toLocaleString('ko-KR')
                            ),
                        },
                        {
                          title: '단가',
                          dataIndex: 'unitPrice',
                          key: 'u',
                          width: 110,
                          align: 'right',
                          render: (v: number | undefined) =>
                            v ? won(v) : <Text type="secondary">—</Text>,
                        },
                        {
                          title: '금액',
                          dataIndex: 'amount',
                          key: 'a',
                          width: 130,
                          align: 'right',
                          render: (v: number) => (
                            <Text type={v < 0 ? 'success' : undefined} strong>
                              {won(v)}
                            </Text>
                          ),
                        },
                      ]}
                      summary={() => (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={4}>
                            <Text strong>공급가액</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="right">
                            <Text strong>
                              {won(
                                (estimate.data?.subtotalAmount ?? 0) -
                                  (estimate.data?.discountAmount ?? 0),
                              )}
                            </Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                    />

                    <Descriptions bordered size="small" column={1}>
                      <Descriptions.Item label="사용료 소계">
                        {won(estimate.data?.subtotalAmount)}
                      </Descriptions.Item>
                      {(estimate.data?.overageAmount ?? 0) > 0 && (
                        <Descriptions.Item label="그중 초과 사용료">
                          <Text type="warning">{won(estimate.data?.overageAmount)}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {' '}
                            — 위쪽 사용량 카드에서 어느 항목이 넘었는지 볼 수 있습니다
                          </Text>
                        </Descriptions.Item>
                      )}
                      {(estimate.data?.discountAmount ?? 0) > 0 && (
                        <Descriptions.Item label="할인">
                          <Text type="success">−{won(estimate.data?.discountAmount)}</Text>
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item label="부가세 (10%)">
                        {won(estimate.data?.vatAmount)}
                      </Descriptions.Item>
                      <Descriptions.Item label="예상 합계">
                        <strong style={{ fontSize: 16 }}>{won(estimate.data?.totalAmount)}</strong>
                      </Descriptions.Item>
                    </Descriptions>

                    <Text type="secondary" style={{ fontSize: 12 }}>
                      기본료와 사용자 요금은 <b>쓴 날짜만큼만</b> 청구합니다(달 중간에
                      시작·해지했다면 내역에 일수가 적힙니다). 초과 사용료는 이미 쓴 양이라
                      날짜로 나누지 않습니다.
                    </Text>
                  </Space>
                )}
              </Card>
            ),
          },
          {
            key: 'invoices',
            label: '청구서',
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {/*
                  청구 이력은 회계 자료다. 결산 때 한 해치를 옮겨 적는
                  대신 받아 가게 한다.
                */}
                <Button
                  icon={<DownloadOutlined />}
                  disabled={(invoices.data ?? []).length === 0}
                  onClick={() =>
                    downloadCsv('청구서', invoices.data ?? [], [
                      { header: '청구서번호', value: (r) => r.invoice_no },
                      { header: '이용시작', value: (r) => csvDate(r.period_start) },
                      { header: '이용종료', value: (r) => csvDate(r.period_end) },
                      { header: '납기일', value: (r) => csvDate(r.due_at) },
                      {
                        header: '상태',
                        value: (r) => INVOICE_STATUS[r.status]?.text ?? r.status,
                      },
                      { header: '청구액', value: (r) => Math.round(r.total_amount) },
                      { header: '수납액', value: (r) => Math.round(r.paid_amount) },
                      {
                        header: '미납액',
                        value: (r) => Math.round(r.total_amount - r.paid_amount),
                      },
                    ])
                  }
                >
                  엑셀 내려받기
                </Button>

                <Table<InvoiceRow>
                  rowKey="invoice_id"
                  columns={invoiceColumns}
                  dataSource={invoices.data ?? []}
                  loading={invoices.isLoading}
                  size="middle"
                  /* 결제 수단·세금계산서 칸이 늘었다. 좁히면 번호가 접힌다 */
                  scroll={{ x: 1100 }}
                  pagination={{ pageSize: 12 }}
                  locale={{ emptyText: '발행된 청구서가 없습니다.' }}
                />
              </Space>
            ),
          },
        ]}
      />

      <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
        사용량은 매일 새벽에 실제 값으로 다시 집계됩니다. 표시된 수치와 청구 금액이 다르면
        문의해 주세요.
      </Paragraph>

      <Modal
        open={Boolean(detailId)}
        title="청구서 상세"
        footer={null}
        onCancel={() => setDetailId(null)}
        width={680}
      >
        {detail.data && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="청구서 번호">
                {String(detail.data.invoice_no)}
              </Descriptions.Item>
              <Descriptions.Item label="상태">
                {INVOICE_STATUS[String(detail.data.status)]?.text ?? String(detail.data.status)}
              </Descriptions.Item>
              <Descriptions.Item label="기간" span={2}>
                {String(detail.data.period_start).slice(0, 10)} ~{' '}
                {String(detail.data.period_end).slice(0, 10)}
              </Descriptions.Item>
            </Descriptions>

            <Table
              rowKey={(r: Record<string, unknown>) => String(r.description)}
              size="small"
              pagination={false}
              dataSource={(detail.data.items as Array<Record<string, unknown>>) ?? []}
              columns={[
                { title: '항목', dataIndex: 'description', key: 'd' },
                {
                  title: '금액',
                  dataIndex: 'amount',
                  key: 'a',
                  width: 140,
                  align: 'right',
                  render: (v: number) => won(v),
                },
              ]}
            />

            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="부가세">
                {won(Number(detail.data.vat_amount))}
              </Descriptions.Item>
              <Descriptions.Item label="합계">
                <strong>{won(Number(detail.data.total_amount))}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="수납">
                {won(Number(detail.data.paid_amount))}
              </Descriptions.Item>
            </Descriptions>
          </Space>
        )}
      </Modal>
    </Space>
  );
}

/**
 * 과금 방식을 한 줄로 설명한다.
 *
 * "사용자 5명 × 9,900원" 처럼 계산 근거가 보여야 한다. 방식 이름(PER_USER)만
 * 보여주면 고객은 자기 요금이 왜 그 금액인지 알 수 없다.
 */
function describeBillingUnit(s: Subscription): string {
  if (s.billing_unit === 'FLAT') return '정액';

  const label = s.unit_label ?? '단위';
  const billable = Math.max(0, s.units - s.included_units);
  const included = s.included_units > 0 ? ` (기본 ${s.included_units} 포함)` : '';

  return `${label} ${s.units}${included} · 과금 ${billable} × ${won(s.unit_price)}`;
}

/** 바이트 지표는 사람이 읽는 단위로 바꾼다. */
/**
 * 초과 단가의 묶음 이름. 1073741824 → "GB", 1 → "개"
 *
 * 「1GB 당 500원」처럼 보여 주어야 고객이 금액을 검산할 수 있다.
 */
function stepLabel(step: number | null, unit: string): string {
  const size = step && step > 1 ? step : 1;
  if (size === 1) return unit === 'BYTES' ? 'B' : '개';

  const units: Array<[number, string]> = [
    [1024 ** 4, 'TB'],
    [1024 ** 3, 'GB'],
    [1024 ** 2, 'MB'],
    [1024, 'KB'],
  ];
  for (const [factor, label] of units) {
    if (size % factor === 0) {
      const count = size / factor;
      return count === 1 ? label : `${count}${label}`;
    }
  }
  return `${size.toLocaleString('ko-KR')}개`;
}

/** 경고 문구에 쓰는 이름. 제품별 지표는 어느 제품인지까지 말해 준다. */
function quotaLabel(q: QuotaRow): string {
  return q.product_name ? `${q.product_name} ${q.metric_name}` : q.metric_name;
}

function formatValue(unit: string, value: number): string {
  if (unit !== 'BYTES') return Math.round(value).toLocaleString('ko-KR');

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = value;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)}${units[i]}`;
}
